/**
 * Pattern metadata for "Patterns and/or Colorblock" selections.
 *
 * The Design Tool persists a pattern *id* (`stripe1`, `polka`, ...) and a tint hex, not display
 * text, so HQ needs these labels to render a proof pill. Kept in step with the Design Tool's
 * `garmentPatternPdfLabels.ts` so a proof reads the same on the PDF and on HQ.
 */
export const PATTERN_LABELS: Record<string, string> = {
  stripe1: 'Stripes 1',
  stripe2: 'Stripes 2',
  polka: 'Polka Dots',
  leopard: 'Leopard',
  camo: 'Camo',
  'custom-upload': 'Custom Pattern',
};

/** Leopard and camo ship as fixed artwork, so their tint hex is not shown. */
export const COLORABLE_PATTERN_IDS = ['stripe1', 'stripe2', 'polka'];

/**
 * Static preview tiles for patterns that are not drawn with CSS. Custom uploads carry their own
 * `patternImageUrl` on the selection and never consult this map.
 */
export const PATTERN_TILE_IMAGES: Record<string, string> = {
  leopard: '/src/assets/garment/leopard-preview.png',
  camo: '/src/assets/garment/camo-preview.png',
};

const PATTERN_SWATCH_WHITE = '#fcfdff';

/** Fallback tints matching the Design Tool swatch previews, used when a row carries no tint. */
const PATTERN_SWATCH_DEFAULT_HEX: Record<string, string> = {
  stripe1: '#ffd5d9',
  stripe2: '#5f202b',
  polka: '#5f202b',
};

export const getPatternLabel = (patternId: string | null | undefined): string => {
  if (!patternId) {
    return '';
  }

  return PATTERN_LABELS[patternId] ?? patternId;
};

export const isColorablePattern = (patternId: string | null | undefined): boolean =>
  !!patternId && COLORABLE_PATTERN_IDS.includes(patternId);

/**
 * Image for a pattern swatch. A custom upload's stored URL wins; leopard/camo fall back to the
 * static HQ tiles. Colourable patterns return null so the CSS background is used instead.
 */
export const getPatternSwatchImageUrl = (
  patternId: string | null | undefined,
  uploadedImageUrl?: string | null
): string | null => {
  if (uploadedImageUrl && uploadedImageUrl.trim() !== '') {
    return uploadedImageUrl.trim();
  }

  if (!patternId) {
    return null;
  }

  return PATTERN_TILE_IMAGES[patternId] ?? null;
};

const PATTERN_SWATCH_BACKGROUNDS: Record<string, (hex: string) => string> = {
  stripe1: (hex) =>
    `repeating-linear-gradient(0deg, ${hex}, ${hex} 4px, ${PATTERN_SWATCH_WHITE} 4px, ${PATTERN_SWATCH_WHITE} 8px)`,
  stripe2: (hex) =>
    `repeating-linear-gradient(0deg, ${hex}, ${hex} 2px, ${PATTERN_SWATCH_WHITE} 2px, ${PATTERN_SWATCH_WHITE} 6px)`,
  polka: (hex) =>
    `radial-gradient(circle, ${hex} 26%, transparent 27%) 0 0 / 8px 8px, radial-gradient(circle, ${hex} 26%, transparent 27%) 4px 4px / 8px 8px, ${PATTERN_SWATCH_WHITE}`,
};

/**
 * CSS background for the small round pill swatch, mirroring the Design Tool previews so the
 * customer sees the same artwork. Returns an empty string for patterns drawn from an SVG asset,
 * which the caller renders as an image instead.
 */
export const getPatternSwatchBackground = (
  patternId: string | null | undefined,
  tintHex?: string | null
): string => {
  const buildBackground = patternId ? PATTERN_SWATCH_BACKGROUNDS[patternId] : undefined;

  if (!buildBackground) {
    return '';
  }

  const hex =
    tintHex && tintHex.trim() !== ''
      ? tintHex.trim()
      : PATTERN_SWATCH_DEFAULT_HEX[patternId as string];

  return buildBackground(hex);
};
